import React, { useState } from 'react';

interface StoryStarterProps {
  theme?: {
    primary: string;
    secondary: string;
    accent: string;
    success: string;
    pink: string;
    blue: string;
  };
  onApplyStarter?: (starter: string) => void;
}

const StoryStarter: React.FC<StoryStarterProps> = ({ 
  theme = {
    primary: '#FF6B35',
    secondary: '#9B59B6',
    accent: '#F39C12',
    success: '#2ECC71',
    pink: '#E91E63',
    blue: '#3498DB'
  }, 
  onApplyStarter = (starter) => console.log('Starter applied:', starter) 
}) => {
  const [selectedCategory, setSelectedCategory] = useState('adventure');
  const [customPrompt, setCustomPrompt] = useState('');
  const [showCustom, setShowCustom] = useState(false);

  const storyCategories = {
    adventure: {
      icon: '⚔️',
      name: 'Epic Adventures',
      color: theme.primary,
      starters: [
        "The old map in the attic showed a path to something incredible...",
        "When the lighthouse stopped working, [character] knew they had to investigate...",
        "The secret door behind the bookshelf led to a world where...",
        "Every night at midnight, strange sounds came from the forest...",
        "The magical compass always pointed toward danger...",
        "A mysterious message in a bottle washed up on the beach..."
      ]
    },
    fantasy: {
      icon: '🏰',
      name: 'Magic & Wonder',
      color: theme.secondary,
      starters: [
        "On their 11th birthday, [character] discovered they could speak to animals...",
        "The dragon everyone feared turned out to be the kindest creature in the kingdom...",
        "When the stars disappeared from the sky, [character] knew magic was in trouble...",
        "The enchanted library only appeared to those who truly needed it...",
        "Every spell [character] tried went wonderfully wrong...",
        "A fairy godmother appeared, but she was having a really bad day..."
      ]
    },
    scifi: {
      icon: '🚀',
      name: 'Space & Future',
      color: theme.blue,
      starters: [
        "The message from space wasn't what Earth expected to receive...",
        "When [character] found the robot in their garage, it asked for help...",
        "Time travel was supposed to be impossible, until [character] accidentally...",
        "The colony ship had been traveling for 100 years when they discovered...",
        "Every kid got a robot companion on their 10th birthday, but [character]'s was different...",
        "The spaceship in the backyard wasn't supposed to be there..."
      ]
    },
    mystery: {
      icon: '🔍',
      name: 'Puzzles & Secrets',
      color: theme.accent,
      starters: [
        "The footprints in the snow led to an empty room...",
        "Every Tuesday, the same mysterious package arrived at school...",
        "When [character] found the old diary, they realized it was writing itself...",
        "The new teacher seemed to know things about the students that no one had told them...",
        "The school's lost and found box contained items from 100 years ago...",
        "A secret code appeared in [character]'s locker every morning..."
      ]
    },
    friendship: {
      icon: '👫',
      name: 'Friends & Fun',
      color: theme.pink,
      starters: [
        "Moving to a new town was hard, until [character] met the kid next door who...",
        "The new student didn't speak any language [character] knew, but somehow they...",
        "When [character]'s best friend started acting strange, they discovered...",
        "The treehouse had been abandoned for years, until two kids decided to...",
        "Every summer, [character] visited their grandparent who had the most amazing stories about...",
        "The friendship bracelet [character] made started glowing at midnight..."
      ]
    },
    animals: {
      icon: '🐾',
      name: 'Animal Adventures',
      color: theme.success,
      starters: [
        "The stray dog seemed to understand exactly what [character] was thinking...",
        "When the animals in the zoo started acting strange, [character] realized...",
        "The bird that landed on [character]'s windowsill carried a tiny message...",
        "Every animal in the forest was gathering in one place, and [character] needed to know why...",
        "The pet that [character] always wanted turned out to be more magical than they imagined...",
        "A talking cat appeared at [character]'s door asking for help..."
      ]
    }
  };

  const randomStarters = [
    "What if gravity stopped working for just one day?",
    "The elevator buttons included floors that didn't exist...",
    "Every mirror in the house showed a different reflection...",
    "The homework assignment was to solve a real mystery...",
    "When [character] sneezed, flowers grew wherever the sneeze landed...",
    "A door to yesterday appeared in [character]'s bedroom...",
    "The wifi password at school turned out to be a magic spell..."
  ];

  const getRandomStarter = () => {
    const allStarters = Object.values(storyCategories).flatMap(cat => cat.starters).concat(randomStarters);
    return allStarters[Math.floor(Math.random() * allStarters.length)];
  };

  const processStarter = (starter: string) => {
    return starter.replace('[character]', '[YOUR CHARACTER\'S NAME]');
  };

  const applyStarter = (starter: string) => {
    onApplyStarter(processStarter(starter));
    // Add a fun celebration effect
    const button = document.querySelector('[data-applied="true"]') as HTMLElement;
    if (button) {
      button.style.transform = 'scale(1.1)';
      setTimeout(() => {
        button.style.transform = 'scale(1)';
      }, 200);
    }
  };

  return (
    <div style={{
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      background: `linear-gradient(135deg, ${theme.secondary} 0%, ${theme.primary} 50%, ${theme.accent} 100%)`,
      minHeight: '100vh',
      padding: '16px'
    }}>
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          background: 'white',
          borderRadius: '25px',
          padding: '30px',
          marginBottom: '25px',
          boxShadow: '0 15px 40px rgba(0,0,0,0.15)',
          border: `4px solid ${theme.primary}`,
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Floating decorative elements */}
          <div style={{
            position: 'absolute', top: '10%', left: '8%', width: '30px', height: '30px',
            background: theme.pink, borderRadius: '50%', opacity: 0.15
          }} />
          <div style={{
            position: 'absolute', bottom: '15%', right: '10%', width: '25px', height: '25px',
            background: theme.success, borderRadius: '50%', opacity: 0.2
          }} />
          
          <div style={{
            width: '80px', height: '80px',
            background: `linear-gradient(135deg, ${theme.primary}, ${theme.accent})`,
            borderRadius: '25px', display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 20px', color: 'white', boxShadow: '0 8px 25px rgba(0,0,0,0.2)',
            fontSize: '35px'
          }}>
            ✨
          </div>
          
          <h1 style={{
            fontSize: '2.5rem', fontWeight: '900', margin: '0 0 12px 0',
            background: `linear-gradient(45deg, ${theme.primary}, ${theme.secondary})`,
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'
          }}>
            Story Starter Magic!
          </h1>
          <p style={{ fontSize: '1.2rem', color: '#666', fontWeight: '600', margin: 0 }}>
            Get amazing ideas to start your epic story!
          </p>
        </div>

        {/* Quick Random Button */}
        <div style={{
          background: 'white', borderRadius: '20px', padding: '25px', marginBottom: '25px',
          boxShadow: '0 12px 35px rgba(0,0,0,0.1)', border: `3px solid ${theme.accent}`
        }}>
          <button 
            onClick={() => applyStarter(getRandomStarter())}
            data-applied="true"
            style={{ 
              width: '100%', 
              padding: '20px', 
              background: `linear-gradient(135deg, ${theme.primary}, ${theme.accent}, ${theme.pink})`, 
              color: 'white', 
              border: 'none', 
              borderRadius: '18px', 
              cursor: 'pointer', 
              fontSize: '20px', 
              fontWeight: '800',
              boxShadow: '0 8px 25px rgba(0,0,0,0.2)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
              e.currentTarget.style.boxShadow = '0 12px 35px rgba(0,0,0,0.25)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0) scale(1)';
              e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.2)';
            }}
          >
            🎲 Surprise Me With A Story!
          </button>
        </div>

        {/* Category Selection */}
        <div style={{
          background: 'white', borderRadius: '20px', padding: '25px', marginBottom: '25px',
          boxShadow: '0 12px 35px rgba(0,0,0,0.1)', border: `3px solid ${theme.secondary}`
        }}>
          <h3 style={{ 
            margin: '0 0 20px 0', fontSize: '1.8rem', fontWeight: '800',
            background: `linear-gradient(45deg, ${theme.secondary}, ${theme.pink})`,
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            textAlign: 'center'
          }}>
            Choose Your Adventure Type!
          </h3>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', 
            gap: '15px' 
          }}>
            {Object.entries(storyCategories).map(([key, category]) => (
              <button
                key={key}
                onClick={() => setSelectedCategory(key)}
                style={{
                  background: selectedCategory === key 
                    ? `linear-gradient(135deg, ${category.color}, ${category.color}dd)` 
                    : 'white',
                  border: `3px solid ${selectedCategory === key ? category.color : '#E5E5E5'}`,
                  padding: '20px 15px',
                  borderRadius: '18px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '700',
                  color: selectedCategory === key ? 'white' : '#333',
                  transition: 'all 0.3s ease',
                  textAlign: 'center',
                  boxShadow: selectedCategory === key 
                    ? `0 8px 25px ${category.color}40` 
                    : '0 4px 15px rgba(0,0,0,0.08)'
                }}
                onMouseEnter={(e) => {
                  if (selectedCategory !== key) {
                    e.currentTarget.style.transform = 'translateY(-3px)';
                    e.currentTarget.style.borderColor = category.color;
                    e.currentTarget.style.boxShadow = `0 8px 25px ${category.color}30`;
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedCategory !== key) {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.borderColor = '#E5E5E5';
                    e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.08)';
                  }
                }}
              >
                <div style={{ fontSize: '32px', marginBottom: '8px' }}>{category.icon}</div>
                <div style={{ fontSize: '13px', lineHeight: '1.2' }}>{category.name}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Story Starters for Selected Category */}
        <div style={{
          background: 'white', borderRadius: '20px', padding: '25px', marginBottom: '25px',
          boxShadow: '0 12px 35px rgba(0,0,0,0.1)', 
          border: `3px solid ${storyCategories[selectedCategory as keyof typeof storyCategories].color}`
        }}>
          <h3 style={{ 
            margin: '0 0 20px 0', fontSize: '1.6rem', fontWeight: '800',
            color: storyCategories[selectedCategory as keyof typeof storyCategories].color,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px',
            textAlign: 'center'
          }}>
            {storyCategories[selectedCategory as keyof typeof storyCategories].icon} 
            {storyCategories[selectedCategory as keyof typeof storyCategories].name} Stories!
          </h3>
          
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: '12px',
            maxHeight: '350px',
            overflowY: 'auto',
            padding: '5px'
          }}>
            {storyCategories[selectedCategory as keyof typeof storyCategories].starters.map((starter, index) => (
              <div 
                key={index}
                style={{ 
                  background: `${storyCategories[selectedCategory as keyof typeof storyCategories].color}10`, 
                  padding: '18px', 
                  borderRadius: '15px',
                  cursor: 'pointer',
                  border: `2px solid ${storyCategories[selectedCategory as keyof typeof storyCategories].color}20`,
                  transition: 'all 0.3s ease',
                  position: 'relative'
                }}
                onClick={() => applyStarter(starter)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = `${storyCategories[selectedCategory as keyof typeof storyCategories].color}20`;
                  e.currentTarget.style.borderColor = storyCategories[selectedCategory as keyof typeof storyCategories].color;
                  e.currentTarget.style.transform = 'translateX(8px)';
                  e.currentTarget.style.boxShadow = `0 8px 25px ${storyCategories[selectedCategory as keyof typeof storyCategories].color}30`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = `${storyCategories[selectedCategory as keyof typeof storyCategories].color}10`;
                  e.currentTarget.style.borderColor = `${storyCategories[selectedCategory as keyof typeof storyCategories].color}20`;
                  e.currentTarget.style.transform = 'translateX(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <p style={{ margin: 0, fontSize: '16px', lineHeight: '1.5', color: '#333', fontWeight: '600' }}>
                  "{processStarter(starter)}"
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Custom Prompt Section */}
        <div style={{
          background: 'white', borderRadius: '20px', padding: '25px', marginBottom: '25px',
          boxShadow: '0 12px 35px rgba(0,0,0,0.1)', border: `3px solid ${theme.success}`
        }}>
          <button
            onClick={() => setShowCustom(!showCustom)}
            style={{
              width: '100%',
              background: showCustom 
                ? `linear-gradient(135deg, ${theme.success}, ${theme.blue})` 
                : 'white',
              color: showCustom ? 'white' : theme.success,
              border: `3px solid ${theme.success}`,
              padding: '18px',
              borderRadius: '15px',
              cursor: 'pointer',
              fontSize: '18px',
              fontWeight: '800',
              marginBottom: showCustom ? '20px' : '0',
              transition: 'all 0.3s ease',
              boxShadow: showCustom ? `0 8px 25px ${theme.success}40` : '0 4px 15px rgba(0,0,0,0.08)'
            }}
          >
            {showCustom ? '✨ Hide' : '🎨 Create'} Your Own Starter!
          </button>
          
          {showCustom && (
            <div>
              <textarea
                value={customPrompt}
                onChange={(e) => setCustomPrompt(e.target.value)}
                placeholder="Write your own amazing story starter... Make it exciting and leave room for adventure! Remember to make it mysterious and fun!"
                style={{
                  width: '100%',
                  height: '120px',
                  padding: '18px',
                  border: `3px solid ${theme.success}40`,
                  borderRadius: '15px',
                  fontSize: '16px',
                  marginBottom: '15px',
                  resize: 'vertical',
                  boxSizing: 'border-box',
                  outline: 'none',
                  fontFamily: 'inherit'
                }}
                onFocus={(e) => e.target.style.borderColor = theme.success}
                onBlur={(e) => e.target.style.borderColor = theme.success + '40'}
              />
              <button
                onClick={() => {
                  if (customPrompt.trim()) {
                    applyStarter(customPrompt);
                    setCustomPrompt('');
                    setShowCustom(false);
                  }
                }}
                disabled={!customPrompt.trim()}
                style={{
                  width: '100%',
                  background: customPrompt.trim() 
                    ? `linear-gradient(135deg, ${theme.success}, ${theme.blue})` 
                    : '#ccc',
                  color: 'white',
                  border: 'none',
                  padding: '16px',
                  borderRadius: '12px',
                  cursor: customPrompt.trim() ? 'pointer' : 'not-allowed',
                  fontSize: '18px',
                  fontWeight: '700',
                  boxShadow: customPrompt.trim() ? `0 6px 20px ${theme.success}40` : 'none',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  if (customPrompt.trim()) {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = `0 8px 25px ${theme.success}50`;
                  }
                }}
                onMouseLeave={(e) => {
                  if (customPrompt.trim()) {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = `0 6px 20px ${theme.success}40`;
                  }
                }}
              >
                🚀 Use This Awesome Starter!
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StoryStarter;